/**
 * iter87.199.43 · TOTP sign-in second step.
 *
 * Rendered when POST /auth/login returns `{requires_2fa: true,
 * challenge_token, expires_in, admin_email}`. Collects a 6-digit code
 * (or, via toggle, a single-use backup code), calls
 * POST /auth/login-totp, and hands the full session payload back to
 * the parent so it can persist the token + redirect exactly like the
 * password-only path.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, KeyRound } from "lucide-react";
import { api } from "@/lib/api";

export default function TotpChallengeStep({ challenge, onSuccess, onCancel }) {
    const [code, setCode] = useState("");
    const [backupCode, setBackupCode] = useState("");
    const [useBackup, setUseBackup] = useState(false);
    const [busy, setBusy] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(challenge?.expires_in || 300);

    useEffect(() => {
        if (secondsLeft <= 0) return;
        const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [secondsLeft]);

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        const payload = { challenge_token: challenge.challenge_token };
        if (useBackup) {
            if (!backupCode.trim()) return;
            payload.backup_code = backupCode.trim();
        } else {
            if (code.length !== 6) return;
            payload.code = code;
        }
        setBusy(true);
        try {
            const r = await api.authLoginTotp(payload);
            onSuccess(r);
        } catch (err) {
            const detail = err?.response?.data?.detail || "Invalid code";
            toast.error(detail);
            setBusy(false);
        }
    };

    const mm = Math.floor(secondsLeft / 60);
    const ss = String(secondsLeft % 60).padStart(2, "0");
    const expired = secondsLeft <= 0;

    return (
        <div className="brutal-card-light p-4 space-y-4" data-testid="totp-challenge-step">
            <div className="flex items-start gap-2">
                <ShieldCheck className="w-5 h-5 text-cyber shrink-0 mt-0.5" />
                <div>
                    <p className="text-[11px] font-black uppercase tracking-widest text-cyber">
                        Two-factor code required
                    </p>
                    <p className="text-xs text-zinc-300 mt-1" data-testid="totp-challenge-admin-email">
                        {challenge?.admin_email}
                    </p>
                </div>
            </div>

            <form onSubmit={submit} className="space-y-3">
                {!useBackup && (
                    <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-zinc-300 block mb-1">
                            Open your authenticator app — enter the current 6-digit code
                        </label>
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            pattern="\d{6}"
                            autoFocus
                            autoComplete="one-time-code"
                            value={code}
                            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            className="brutal-input w-full font-mono tracking-widest text-lg"
                            data-testid="totp-challenge-input"
                        />
                    </div>
                )}
                {useBackup && (
                    <div>
                        <label className="text-[11px] font-black uppercase tracking-widest text-zinc-300 block mb-1">
                            Enter one of your 10 backup codes
                        </label>
                        <input
                            type="text"
                            autoFocus
                            autoComplete="off"
                            value={backupCode}
                            onChange={(e) => setBackupCode(e.target.value.trim())}
                            className="brutal-input w-full font-mono"
                            placeholder="e.g. J8mQ_pT7-a2b"
                            data-testid="totp-challenge-backup-input"
                        />
                    </div>
                )}
                <div className="flex items-center justify-between text-[11px]">
                    <button
                        type="button"
                        onClick={() => { setUseBackup((v) => !v); setCode(""); setBackupCode(""); }}
                        className="text-cyber hover:underline flex items-center gap-1"
                        data-testid="totp-challenge-backup-toggle"
                    >
                        <KeyRound className="w-3 h-3" />
                        {useBackup ? "Use authenticator code instead" : "Use a backup code instead"}
                    </button>
                    <span
                        className={expired ? "text-red-400 font-black" : "text-zinc-500"}
                        data-testid="totp-challenge-timer"
                    >
                        {expired ? "Expired — request a new code" : `${mm}:${ss} left`}
                    </span>
                </div>
                <div className="flex gap-2 pt-1">
                    <button
                        type="submit"
                        disabled={busy || expired || (useBackup ? !backupCode.trim() : code.length !== 6)}
                        className="brutal-btn-primary text-xs px-3 py-2 flex-1"
                        data-testid="totp-challenge-submit"
                    >
                        {busy ? "Verifying…" : "Verify & sign in"}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="brutal-btn-secondary text-xs px-3 py-2"
                        data-testid="totp-challenge-cancel"
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
}
